import type { StoredFormState } from '../types';

/**
 * Loads form state from localStorage
 */
export function loadFormState(localStorageKey: string): Partial<StoredFormState> | null {
  try {
    // Check if window is available (SSR check)
    if (typeof window === 'undefined' || !window.localStorage) {
      return null;
    }
    const raw = window.localStorage.getItem(localStorageKey);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    console.error('Failed to read form state from localStorage', e);
    return null;
  }
}

/**
 * Saves form state to localStorage
 */
export function saveFormState(localStorageKey: string, state: StoredFormState): void {
  try {
    // Check if window is available (SSR check)
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }
    const payload = JSON.stringify(state);
    // Check size before saving (localStorage limit is typically 5-10MB)
    const sizeInMB = new Blob([payload]).size / (1024 * 1024);
    if (sizeInMB > 4) {
      console.warn('Form state is getting large:', sizeInMB.toFixed(2), 'MB');
    }
    window.localStorage.setItem(localStorageKey, payload);
  } catch (e) {
    if (e instanceof DOMException && e.name === 'QuotaExceededError') {
      console.error('localStorage quota exceeded. Consider clearing old data.');
    } else {
      console.error('Failed to write form state to localStorage', e);
    }
  }
}

/**
 * Clears form state from localStorage
 */
export function clearFormState(localStorageKey: string): void {
  try {
    // Check if window is available (SSR check)
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }
    window.localStorage.removeItem(localStorageKey);
  } catch (e) {
    console.error('Failed to clear form state from localStorage', e);
  }
}
